import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingOptions,
    DrawingSettingGroup,
    DrawingTypes,
    LineTypes,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { buildAutoscaleInfoFromPrices, getDistanceFromSegment, isCoarsePointer, scaledCoordinate } from '../drawing.helper'
import { AutoscaleInfo, Coordinate, Logical, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { getTimeIndex } from '../../../../util/getTimeIndex'
import { SettingType } from '../drawing.enums'

export type IndicatorLineDrawingType = 'simpleIndicatorLine' | 'infoIndicatorLine'

export type IndicatorLineDrawingState = {
    points: [Point, Point, string][]
    labels?: string[]
}

export type IndicatorLineToolOptions = {
    drawingType: IndicatorLineDrawingType
}

export type IndicatorLineDrawingSettings = {
    lineType: LineTypes,
    indicator: boolean,
    lineColor: ColorValue,
    lineWidth: number,
    data: [Point, Point, string][],
    statsPosition: string,
    showStats: boolean,
    seriesGroupId?: string
    /** Side of the line the label sits on. */
    labelsPosition?: 'Left' | 'Right'
}

export type ScaledCoordinateType = { p1x: number, p2x: number, p1y: number, p2y: number, label: string }

class IndicatorLineDrawingTool extends DrawingBase<IndicatorLineToolOptions, IndicatorLineDrawingSettings, IndicatorLineDrawingState> {
    slopes: number[] = []
    rotations: number[] = []
    intercepts: number[] = []
    scaledCoordinates: ScaledCoordinateType[] = []

    constructor(options: DrawingOptions<IndicatorLineToolOptions, IndicatorLineDrawingSettings, IndicatorLineDrawingState>) {
        super(options)
        this._state.points = options.settings.data ?? []
        setTimeout(() => {
            this.primitive?.requestUpdate()
        }, 0)

    }

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        this.calculateLineAttributes()
        if (!this.scaledCoordinates.length) return

        const dpr = (window.devicePixelRatio || 1)
        const fontSize = 12 * dpr
        const fontFamily = getComputedStyle(document.body).fontFamily || 'sans-serif'
        const gap = 4 * dpr
        const labelOnRight = this._settings.labelsPosition === 'Right'
        const paneWidth = scope.bitmapSize?.width ?? ctx.canvas.width

        this.scaledCoordinates.forEach((scaledCoordinate) => {
            let start = this.getStartCoordinate(scaledCoordinate)
            let end = this.getEndCoordinate(scaledCoordinate)

            if (!start || !end) return

            ctx.beginPath()
            this.setLineAttributes(ctx)
            ctx.moveTo(start.x, start.y)
            ctx.lineTo(end.x, end.y)
            ctx.stroke()
            ctx.closePath()

            if (!scaledCoordinate.label) return
            ctx.font = `${fontSize}px ${fontFamily}`
            ctx.fillStyle = this._settings.lineColor.color
            const textWidth = ctx.measureText(scaledCoordinate.label).width

            // The label sits beside its end of the line. When that end is off screen but the line is
            // not, the label moves to the edge of the pane, just above the line. A line that is
            // entirely off screen keeps its label off screen with it.
            let labelX = labelOnRight ? end.x + gap : start.x - gap - textWidth
            let pinned = false
            if (!labelOnRight && labelX < gap && end.x > gap + textWidth) {
                labelX = gap
                pinned = true
            } else if (labelOnRight && labelX + textWidth > paneWidth - gap && start.x < paneWidth - gap - textWidth) {
                labelX = paneWidth - gap - textWidth
                pinned = true
            }
            ctx.textBaseline = pinned ? 'bottom' : 'middle'
            ctx.fillText(scaledCoordinate.label, labelX, pinned ? start.y - gap / 2 : start.y)
        })
    };

    /** X of the first bar at or after `time`, so a level starts on the first bar of its period. */
    private getLineStartCoordinate(time: Time): Coordinate | null {
        const data = this.getTimescaleData()
        if (!data.length) return null
        if ((time as number) < (data[0].time as number)) return null
        let index = getTimeIndex(data, time as number)
        if (!Number.isFinite(index)) return null
        if ((data[index].time as number) < (time as number) && index + 1 < data.length) index += 1
        return this._chartApi.timeScale().logicalToCoordinate(index as Logical)
    }

    calculateLineAttributes() {
        const { points } = this._state
        if (!points || (points.length === 0)) {
            this.scaledCoordinates = []
            this.slopes = []
            return
        }

        const newScaledCoordinates: ScaledCoordinateType[] = []
        const newSlopes: typeof this.slopes = []
        const newIntercepts: typeof this.intercepts = []
        const newRotations: typeof this.rotations = []

        const firstLoadedTime = this.getTimescaleData()[0]?.time as number | undefined

        points.forEach((point) => {
            let p1x = this.getLineStartCoordinate(point[0].x)
            let p2x = this.timeToCoordinate(point[1].x)
            let p1y = this.priceToCoordinate(point[0].y)
            let p2y = this.priceToCoordinate(point[1].y)
            // A level usually starts at the period open, which is before the first loaded bar on
            // intraday charts; start it from the first loaded bar instead of dropping the whole line.
            if (
                p1x === null && p2x !== null && firstLoadedTime !== undefined
                && (point[0].x as number) < firstLoadedTime
            ) {
                p1x = this.timeToCoordinate(firstLoadedTime as Time)
            }
            if (p1x !== null && p2x !== null && p1y !== null && p2y !== null && p2x >= p1x) {
                p1x = scaledCoordinate(p1x, this.horizontalPixelRatio) as Coordinate
                p1y = scaledCoordinate(p1y, this.verticalPixelRatio) as Coordinate
                p2x = scaledCoordinate(p2x, this.horizontalPixelRatio) as Coordinate
                p2y = scaledCoordinate(p2y, this.verticalPixelRatio) as Coordinate
                newScaledCoordinates.push({ p1x, p1y, p2x, p2y, label: point[2] })
                const slope = (p2y - p1y) / (p2x - p1x)
                newSlopes.push(slope)
                newIntercepts.push(p1y - slope * p1x)
                newRotations.push(Math.atan(slope))
            }
        })
        this.scaledCoordinates = newScaledCoordinates
        this.slopes = newSlopes
    }

    /** Keeps the levels of the periods on screen inside the auto-scaled price range. */
    getAutoscaleInfo(startTimePoint: Logical, endTimePoint: Logical): AutoscaleInfo | null {
        const data = this.getTimescaleData()
        if (!data.length) return null
        const firstLoadedTime = data[0].time as number

        const prices: number[] = []
        this._state.points?.forEach(([start, end]) => {
            const endIndex = getTimeIndex(data, end.x as number)
            if (!Number.isFinite(endIndex)) return
            const startIndex = (start.x as number) < firstLoadedTime ? 0 : getTimeIndex(data, start.x as number)
            if (startIndex > endTimePoint || endIndex < startTimePoint) return
            if (Number.isFinite(start.y)) prices.push(start.y)
        })
        return buildAutoscaleInfoFromPrices(prices)
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        // Same click area as chart series: 4 CSS px for a mouse, 16 for a finger.
        const threshold = (isCoarsePointer() ? 16 : 4) * this.verticalPixelRatio + this._settings.lineWidth / 2

        for (let i = 0; i < this.scaledCoordinates.length; i++) {
            const { p1x, p1y, p2x, p2y } = this.scaledCoordinates[i]
            if (getDistanceFromSegment(scaledX, scaledY, p1x, p1y, p2x, p2y) <= threshold) {
                // Not the bare drawing id: a hit selects the indicator, it must not focus the drawing itself.
                return { externalId: `${this.id}__line-${i}`, zOrder: 'normal', cursorStyle: 'pointer' }
            }
        }
        return null
    }

    getStartCoordinate(scaledCoordinate: ScaledCoordinateType): PointCoordinates | void {
        let { p1x, p1y } = scaledCoordinate

        let start: PointCoordinates = {
            x: p1x,
            y: p1y
        }
        return start
    }

    getEndCoordinate(scaledCoordinate: ScaledCoordinateType): PointCoordinates | void {
        let { p2x, p2y } = scaledCoordinate

        let end: PointCoordinates = {
            x: p2x,
            y: p2y
        }

        return end
    }

    setLineAttributes(ctx: CanvasRenderingContext2D, setLineType = true) {
        let lineDash: number[] = []
        if (this._settings.lineType === 'dashed') {
            lineDash = [Math.max(this._settings.lineWidth * 3, 8), 3]
        } else if (this._settings.lineType === 'dotted') {
            const dotWidth = Math.max(2, this._settings.lineWidth)
            lineDash = [dotWidth, dotWidth]
        }
        setLineType && ctx.setLineDash(lineDash)
        ctx.globalAlpha = this._settings.lineColor.opacity
        ctx.lineWidth = this._settings.lineWidth
        ctx.strokeStyle = this._settings.lineColor.color
    }

    onSettingsUpdate = (settings: Partial<IndicatorLineDrawingSettings>) => {
        if (this._settings.data) {
            this._state.points = this._settings.data?.map((point) => [point[0], point[1], point[2]])
        }
    }

    getAvailableSettings = (): DrawingSettingGroup<IndicatorLineDrawingSettings, SettingValues>[] => {
        const { drawingType } = this.options
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'lineColor',
                    label: 'Line Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#646464',
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineType',
                    label: 'Line Type',
                    type: SettingType.LINE_TYPE,
                    defaultValue: 'normal',
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineWidth',
                    label: 'Line Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 2,
                    custom: true,
                    visible: true
                }
            ]
        }
        ]
    }

    getSettingsValues(): IndicatorLineDrawingSettings {
        const settings = { ...this._settings }
        settings.data = this._state.points
        return settings
    }

    getDrawingType(): DrawingTypes {

        switch (this.options.drawingType) {
            case 'simpleIndicatorLine':
                return 'simpleIndicatorLine'
            case 'infoIndicatorLine':
                return 'infoIndicatorLine'

        }
    }

    getMiniSettings = () => {
        return ['lineColor', 'lineType', 'lineWidth'] as (keyof IndicatorLineDrawingSettings)[]
    }
}

export default IndicatorLineDrawingTool